<script setup lang="ts">
import { ref, watch } from 'vue'
import type { PushApp, PushDevice } from './types'
import { pushAppOptions } from './types'
import { formatTime, providerLabel, statusText } from './usePushCenterState'
import {
  AdminDataTable,
  AdminFilterBar,
  AdminStatusTag,
  RrButton,
  RrInput,
  RrSelect,
  RrTag,
  type TableColumnType
} from '@xjlv2/admin-layout'

const props = withDefaults(
  defineProps<{
    devices?: PushDevice[]
    total: number
    page: number
    pageSize: number
    queryLoading?: boolean
    apps?: PushApp[]
    appCode?: string
    canManage?: boolean
    disableDevice: (deviceId: number) => Promise<void>
  }>(),
  {
    devices: () => [],
    queryLoading: false,
    apps: () => [],
    appCode: '',
    canManage: false
  }
)

const emit = defineEmits<{
  'update:appCode': [code: string]
  search: [keyword: string, appCode: string]
  'page-change': [pageNo: number, pageSize: number]
  refresh: []
  error: [message: string]
}>()

const localAppCode = ref(props.appCode)
watch(
  () => props.appCode,
  (code) => {
    localAppCode.value = code
  }
)

function onAppChange(code: string) {
  emit('update:appCode', code)
}

const keywordInput = ref('')
const disablingDeviceId = ref<number | null>(null)

const columns: TableColumnType<PushDevice>[] = [
  { key: 'userId', title: '用户 ID', width: 100 },
  { key: 'platform', title: '平台', width: 100 },
  { key: 'provider', title: '厂商通道', width: 120 },
  { key: 'device', title: '设备与版本', minWidth: 200 },
  { key: 'token', title: 'Token', minWidth: 180 },
  { key: 'status', title: '状态', width: 100 },
  { key: 'lastSeenAt', title: '最后活跃时间', width: 180 },
  { key: 'actions', title: '操作', width: 100, align: 'right', fixed: 'right' }
]

function handleSearch() {
  emit('search', keywordInput.value.trim(), localAppCode.value)
}

function handleReset() {
  keywordInput.value = ''
  localAppCode.value = ''
  emit('search', '', '')
}

async function handleDisableDevice(device: PushDevice) {
  if (!props.canManage || !device.enabled || disablingDeviceId.value !== null) return
  const deviceLabel = device.deviceId || `#${device.id}`
  if (!window.confirm(`确定禁用设备「${deviceLabel}」吗？禁用后该设备将不再接收推送。`)) {
    return
  }

  disablingDeviceId.value = device.id
  try {
    await props.disableDevice(device.id)
    emit('refresh')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '设备禁用失败')
  } finally {
    disablingDeviceId.value = null
  }
}
</script>

<template>
  <div class="push-tab-pane">
    <AdminFilterBar @submit="handleSearch" @reset="handleReset">
      <label>
        所属应用
        <RrSelect
          v-model:value="localAppCode"
          :options="pushAppOptions(apps)"
          @change="onAppChange"
        />
      </label>
      <label>
        用户 / 设备 ID
        <RrInput
          v-model:value.trim="keywordInput"
          placeholder="输入 clientUserId 或 deviceId 搜索"
          @keyup.enter="handleSearch"
        />
      </label>
    </AdminFilterBar>

    <AdminDataTable
      :columns="columns"
      :data-source="devices"
      :current="page"
      :page-size="pageSize"
      :total="total"
      :loading="queryLoading"
      :empty="devices.length === 0"
      empty-title="暂无活跃设备"
      empty-description="客户端注册推送 Token 后将在此展示。"
      @update:current="(p) => emit('page-change', p, pageSize)"
      @update:page-size="(size) => emit('page-change', 1, size)"
      @refresh="handleSearch"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'userId'">
          <code>#{{ record.userId }}</code>
        </template>
        <template v-else-if="column.key === 'platform'">
          <RrTag tone="neutral" :label="record.platform" />
        </template>
        <template v-else-if="column.key === 'provider'">
          <RrTag tone="primary" :label="providerLabel(record.provider)" />
        </template>
        <template v-else-if="column.key === 'device'">
          <div class="template-content-cell">
            <span>{{ record.deviceId || '-' }}</span>
            <small>App v{{ record.appVersion }} / OS {{ record.osVersion }}</small>
          </div>
        </template>
        <template v-else-if="column.key === 'token'">
          <code class="masked-code">{{ record.tokenMasked }}</code>
        </template>
        <template v-else-if="column.key === 'status'">
          <AdminStatusTag
            :text="statusText(record.enabled)"
            :tone="record.enabled ? 'success' : 'neutral'"
          />
        </template>
        <template v-else-if="column.key === 'lastSeenAt'">
          <span class="text-time">{{ formatTime(record.lastSeenAt) }}</span>
        </template>
        <template v-else-if="column.key === 'actions'">
          <RrButton
            v-if="record.enabled && canManage"
            html-type="button"
            size="small"
            danger
            :loading="disablingDeviceId === record.id"
            @click="handleDisableDevice(record)"
          >
            禁用
          </RrButton>
        </template>
      </template>
    </AdminDataTable>
  </div>
</template>

<style scoped>
.push-tab-pane {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.masked-code {
  font-family: var(--rr-font-mono, monospace);
  font-size: 12px;
  color: var(--rr-color-text);
  background: var(--rr-color-canvas);
  padding: 2px 6px;
  border-radius: 4px;
}

.template-content-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.template-content-cell small {
  color: var(--rr-color-text-muted);
  font-size: 11px;
}

.text-time {
  font-size: 12px;
  color: var(--rr-color-text-muted);
}
</style>
